import React, { useEffect } from "react";
import "./InternalK1Hierarchy.css";
import { Col, Modal, Row } from "react-bootstrap";
import { useState, useCallback } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useNodesState,
  useEdgesState,
  MarkerType,
  Controls,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Dagre from '@dagrejs/dagre';
import { getHierarchy } from "./InternalK1Hierarchy.service";
import { Bars } from "react-loader-spinner";

interface InternalK1HierarchyProps {
  show: boolean;
  onHide: () => void;
  returnid: {} | null; // or number, depending on your data
  refresh: () => void;
  clientMasterId?: string | null;
}

/**
 * InternalK1Hierarchy Component
 */
const initialNodes = [
  { id: 'a', position: { x: 0, y: 0 }, data: { label: 'Node A' } },
  { id: 'b', position: { x: 0, y: 100 }, data: { label: 'Node B' } },

];

const initialEdges = [
  { id: 'a->b', type: 'custom-edge', source: 'a', target: 'b' },
];

const proOptions = { hideAttribution: true };

const InternalK1Hierarchy: React.FC<InternalK1HierarchyProps> = ({
  show,
  onHide,
  returnid,
  refresh,
  clientMasterId
}) => {
  const [loadHierarchy, setloadHierarchy] = useState(false);
  const [hierarchyData, setHierarchyData] = useState<any[]>([]);
  const [sliderEnabled, setSliderEnabled] = useState<boolean>(true);
  
  useEffect(() => {
    getData()
  }, [sliderEnabled])

  const getData = async () => {
    setloadHierarchy(true)
    if (returnid) {
      if (sliderEnabled) {
        const onlyParentsObject: any = await getHierarchy(returnid, clientMasterId);
        setHierarchyData(onlyParentsObject.immediate_nodes)
      }
      else if(!sliderEnabled) {
        const hierarchyData: any = await getHierarchy(returnid, clientMasterId);
        console.log(hierarchyData);
        setHierarchyData(hierarchyData.nodes);
      }
      
    }

    setloadHierarchy(false)
  }
  return (
    <>

      <Modal
        show={show}
        onHide={() => onHide()}
        size="lg"
        backdrop="static"
        centered
        className="hierarchyModal"
        dialogClassName="hierarchyModalDialog"
        contentClassName="hierarchyModalContent"
      >
        <Modal.Header className="hierarchyHeader">
          <Row className="w-100 m-0 p-0">
          <Col md={6}>
            <h4 className="header ms-4">Internal K1 Hierarchy</h4>
          </Col>
          <Col md={5} className="d-flex align-items-center justify-content-end">
            <label className="me-1">All Collections</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={sliderEnabled}
                onChange={() => setSliderEnabled(!sliderEnabled)}
                disabled={false}
                style={{ appearance: "none" }}
              />
              <span className={`super-admin-slider super-admin-round ${false ? "disabled" : ""}`} />
            </label>
            <label className="ms-2">Only Parents</label>            
          </Col>
          <Col md={1}>
            <button
              type="button"
              className="closeButton"
              onClick={() => onHide()}
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </Col>
          </Row>
        </Modal.Header>
        <Modal.Body className="hierarchyModalBody">
          <Row className='no-gutter'>
            <div style={{ height: "80vh", width: "100%" }}>
              {
                returnid === undefined ?
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    width: "100%"
                  }}>
                    <h3>Please select a Return Id</h3>
                  </div>
                  :
                  <>
                    {
                      loadHierarchy ?
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          width: "100%"
                        }}>
                          <Bars
                            height="80"
                            width="80"
                            color="var(--theme1_primary)"
                            ariaLabel="bars-loading"
                            wrapperStyle={{}}
                            wrapperClass=""
                            visible={true}
                          />
                          <h6 style={{ marginBottom: 0, marginRight: 20 }}>Loading ...</h6>
                        </div>
                        :
                        <ReactFlowProvider>
                          <LayoutFlow
                            hierarchydata={hierarchyData}
                          />
                        </ReactFlowProvider>
                    }
                  </>
              }

            </div>
          </Row>

        </Modal.Body>
      </Modal>
    </>
  );
};

const getLayoutedElements = (nodes: any, edges: any, options: any) => {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: options.direction });

  edges?.forEach((edge: any) => g.setEdge(edge.source, edge.target));
  nodes?.forEach((node: any) =>
    g.setNode(node.id, {
      ...node,
      width: node.measured?.width ?? 150,
      height: node.measured?.height ?? 50,
    }),
  );

  Dagre.layout(g);
  // console.log("inside")
  return {
    nodes: nodes?.map((node: any) => {
      const position = g.node(node.id);
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      const x = position.x - (node.measured?.width ?? 0) / 2;
      const y = position.y - (node.measured?.height ?? 0) / 2;

      return { ...node, position: { x, y } };
    }),
    edges,
  };
};

const LayoutFlow = ({ hierarchydata }: any) => {

  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const onLayout = useCallback(
    (direction: any) => {
      // console.log(nodes);
      // const { flowNodes, flowEdges } = transformApiDataToFlow(data);
      const layouted = getLayoutedElements(nodes, edges, { direction });

      setNodes([...layouted.nodes]);
      setEdges([...layouted.edges]);

      fitView();
    },
    [nodes, edges, fitView],
  );


  function transformApiDataToFlow(nodes: any) {
    const defaultNodeWidth = 160;
    const defaultNodeHeight = 40;
    const flowNodes = nodes?.map((node: any) => ({
      id: `n${node.id}`,
      data: {
        partnership: node.name ?? 'Partnership',
        status: node.status ?? 'Status',
        type: node.type ?? 'Type',
        signer: (node.signer ?? '').trim(),            
        location: (node.location ?? '').trim(),        
      },
      position: { x: 0, y: 0 },
      sourcePosition: 'bottom',
      targetPosition: 'top',
      type: 'textRenderer',
      width: defaultNodeWidth,
      height: defaultNodeHeight
    }));

    const themeColor = getComputedStyle(document.documentElement).getPropertyValue('--theme1_tertiary').trim();

    const flowEdges = nodes?.flatMap((node: any) =>
      node.inputs?.map((inputId: any) => ({
        id: `e${inputId}-${node.id}`,
        source: `n${inputId}`,
        target: `n${node.id}`,
        type: 'step',
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 15,
          height: 15,
          color: themeColor || '#000000',
        },
        style: {
          strokeWidth: 1,
          stroke: themeColor || '#000000',
        },
      }))
    );

    return { flowNodes, flowEdges };
  }
  const fetchData = async () => {
    // console.log(hierarchydata);
    const { flowNodes, flowEdges } = transformApiDataToFlow(hierarchydata);

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(flowNodes, flowEdges, { direction: 'TB' });

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
    fitView(); // Optional: zoom to fit
  }

  const nodeTypes = {
    textRenderer: TextRendererNode,
  };

  const onInit = useCallback(() => {
    fetchData();
  }, [onLayout]);

  return (

    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onInit={onInit}
      fitView
      proOptions={proOptions}
      nodeTypes={nodeTypes}
    >
      <Controls />
    </ReactFlow>
  );

}

export function TextRendererNode({ data }: any) {
  return (
    <div className="NodeStyle">
      {/* Info badge + tooltip (top-left) */}
      <div className="InfoWrapper" onMouseDown={(e) => e.stopPropagation()}>
        <span
          className="InfoBadge"
          aria-label="Node info"
          role="img"
        >
          i
        </span>
        <div className="InfoTooltip">
          <div><strong>Signer:</strong> {data?.signer}</div>
          <div><strong>Location:</strong> {data?.location}</div>
        </div>
      </div>

      <p className="SubTitle top-right">{data?.type || 'Type'}</p>
      <p className="MainTitle">
        {data?.partnership || 'Partnership'}
      </p>
      <p className="SubTitle">{data?.status || 'Status'}</p>

      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} style={{ bottom: '-3px' }} />
    </div>
  );
}

export default InternalK1Hierarchy;


